import {
  DatabasePlantRecordField,
  DatabasePlantRecordMetadata,
  Plant,
  PlantCollection,
  PlantWithProjectName,
} from '../entities/plant'
import {supabase} from './BackendService'
import urlProjectPhoto0 from 'url:../../public/assets/images/image1.jpg'
import dayjs from 'dayjs'
import type {FilterValue} from 'antd/es/table/interface'
import {ArrayElementOf, cached, cachedById, comparePlantFields} from './util'
import {NodeConfig} from 'konva/lib/Node'
import {instance as journalService} from './JournalService'
import {instance as projectService} from './ProjectsService'
import {ElementWithVisibility} from '../components/canvas/CanvasViewEditorData'
import {TypedElement} from '../entities/project'
import {Language} from './language'

export type PlantedPlantsFilters = {
  date?: {startDate: Date; endDate: Date}
  projects?: string[]
  projectId?: number
  plantName?: string
  startPlantingDate?: Date
  endPlantingDate?: Date
  collections?: number[]
  lifecycle?: Record<string, boolean>
  hideGardenPlanVariantPlants?: boolean
}

const commonNameField = 'Общераспространенное название'
const varietyField = 'Сорт_eng'
const varietyFieldLocal = 'Сорт_ru'

const queryFields = [
  commonNameField,
  varietyField,
  varietyFieldLocal,
  'Научное (латинское) название',
  'Другие названия',
]

export const plantsSortingFields = ['plantedDate', 'name', 'project'] as const
export type PlantsSortingField = (typeof plantsSortingFields)[number]

export type RemovedDatabaseRecordFields = 'Семейство_ru' | 'Семейство_la' | 'Сорт_eng' | 'Сорт_ru'

export type PlantFields = Omit<DatabasePlantRecord['fields'], RemovedDatabaseRecordFields> & {
  Семейство: string
  Сорт: string
}

export function translateTextBaseField(
  fieldName: keyof PlantFields,
  basePlantFields: Partial<DatabasePlantRecord['fields']>,
  context: {language: Language}
) {
  const fieldMetadata = DatabasePlantRecordMetadata[fieldName] as DatabasePlantRecordField

  // the stored record also carries '[EN] …'/'[PL] …' translation columns, and PlantFields
  // adds Семейство/Сорт - none of which the declared field list models
  const fields = basePlantFields as Record<string, any>

  let basePlantFieldValue = null

  if (context.language === 'en' && Object.keys(basePlantFields).includes(`[EN] ${fieldName}`)) {
    basePlantFieldValue = fields[`[EN] ${fieldName}`]
  } else if (
    context.language === 'en' &&
    Object.keys(basePlantFields).includes(`[PL] ${fieldName}`)
  ) {
    basePlantFieldValue = fields[`[PL] ${fieldName}`]
  } else {
    basePlantFieldValue =
      fieldMetadata?.getValueFromDbRecord?.(basePlantFields, context) ?? fields[fieldName]
  }

  return basePlantFieldValue
}

export function prepareOverwrittenBaseFields(
  plantFields: Partial<PlantFields>,
  basePlantFields: Partial<DatabasePlantRecord['fields']>,
  context: {language: Language},
  includeMatchingFields = false
) {
  const result: Partial<Record<keyof PlantFields, any>> = {}

  for (const key of Object.keys(DatabasePlantRecordMetadata) as (keyof PlantFields)[]) {
    const basePlantFieldValue = translateTextBaseField(key, basePlantFields, context)

    const newFieldValue = plantFields[key]

    if (includeMatchingFields) {
      if (newFieldValue) {
        result[key] = newFieldValue
      } else {
        result[key] = basePlantFieldValue
      }
    } else {
      if (newFieldValue && !comparePlantFields(newFieldValue, basePlantFieldValue)) {
        //сравнение массивов
        result[key] = newFieldValue
      }
    }
  }

  return result
}

export function parseFilters(filters: Record<string, FilterValue>): PlantedPlantsFilters {
  return {
    date:
      filters.dateOfPlantingUtc?.[0] && filters.dateOfPlantingUtc?.[1]
        ? {
            startDate: new Date(filters.dateOfPlantingUtc[0] as string),
            endDate: new Date(filters.dateOfPlantingUtc[1] as string),
          }
        : undefined,
    plantName: filters.name ? (filters.name[0] as string) : undefined,
    projects: filters.project as string[],
    collections: filters.collections as number[],
  }
}

// every caller below runs behind an authenticated route, so a session is always present
async function currentUserId() {
  return (await supabase.auth.getSession()).data.session!.user.id
}

export class PlantsService {
  public DEFAULT_PER_PAGE = 12

  public getProjectPlants = cachedById<Plant[], string>(
    async (projectId: string, language?: Language) => {
      let {data: plantedPlantsFromDB, error} = await supabase
        .from('plantedPlants')
        .select(
          `id, name, dateOfPlantingUtc, photos, comment, plantBaseId, overwrittenBaseFields, projectId, coordinates, userId, confidenceInPlantVariety, isPlantDeleted, template, basePlant:plantBaseId( id, fields, image_id ), project:projectId( name ), gardenPlanVariantId, collections:plantedPlantsVSCollection( ...plantedPlantsCollections(id, color, name) ), todos:plantedPlantsVSToDos(...toDos(id))`
        )
        .eq('projectId', projectId)
        .eq('isPlantDeleted', false)
        .order('name')

      const plantedPlants = (plantedPlantsFromDB ?? []).map((plantFromDB) => {
        const {basePlant, project, ...plant} = plantFromDB

        const userPlantFields = plant.overwrittenBaseFields || {}
        const basePlantFields = ((basePlant as any) || {})?.fields || {}

        const combinedPlantFields = prepareOverwrittenBaseFields(
          userPlantFields,
          basePlantFields,
          {language: language as Language},
          true
        )

        const processedPlant: PlantWithProjectName = {
          ...plant,
          titlePhotoName: (basePlant as any)?.image_id,
          fields: combinedPlantFields,
          projectName: (project as any)?.name,
        }
        return processedPlant
      })

      return plantedPlants as Plant[]
    }
  )

  public getPlantById = cachedById<
    {success: true; plant: PlantWithProjectName} | {success: false; error: 'notFound'}
  >(async (plantId: number, language?: Language) => {
    let {data: plants, error} = await supabase
      .from('plantedPlants')
      .select(
        'id, name, dateOfPlantingUtc, photos, comment, plantBaseId, overwrittenBaseFields, projectId, coordinates, userId, confidenceInPlantVariety, isPlantDeleted, template, basePlant:plantBaseId( id, fields, image_id ), project:projectId( name ), gardenPlanVariantId, collections:plantedPlantsVSCollection( ...plantedPlantsCollections(id, color, name) ), todos:plantedPlantsVSToDos(...toDos(id))'
      )
      .eq('id', plantId)

    const firstPlant = (plants as any)[0]

    if (!firstPlant) {
      return {success: false, error: 'notFound'}
    }

    const {basePlant, project, ...userPlant} = firstPlant

    if (basePlant) {
      // for (const fieldName of Object.keys(basePlant.fields)) {
      //   if (Array.isArray(basePlant.fields[fieldName])) {
      //     basePlant.fields[fieldName] = basePlant.fields[fieldName].map((s: string) =>
      //       s.toLowerCase()
      //     )
      //   }
      // }
    }

    const userPlantFields = userPlant.overwrittenBaseFields || {}
    const basePlantFields = ((basePlant as any) || {})?.fields || {}

    const combinedPlantFields = prepareOverwrittenBaseFields(
      userPlantFields,
      basePlantFields,
      {language: language as Language},
      true
    )

    const plant: PlantWithProjectName = {
      ...userPlant,
      titlePhotoName: (basePlant as any)?.image_id,
      fields: combinedPlantFields,
      projectName: (project as any)?.name,
      // collections: (collections as any[]).map((collection) => ({
      //   id: collection.collectionId,
      //   name: collection.name,
      // })),
    }

    return {success: true, plant}
  })

  public getUserPlants = cached(async () => {
    let {data: plantedPlantsWithProjectName, error} = await supabase
      .from('plantedPlants')
      .select(
        'id, name, dateOfPlantingUtc, photos, comment, plantBaseId, overwrittenBaseFields, projectId, coordinates, userId, confidenceInPlantVariety, isPlantDeleted, template, basePlant:plantBaseId( id, fields, image_id ), project:projectId( name ), gardenPlanVariantId, collections:plantedPlantsVSCollection( ...plantedPlantsCollections(id, color, name) )'
      )
      .eq('userId', await currentUserId())
      .eq('isPlantDeleted', false)

    return (plantedPlantsWithProjectName ?? []).map(({basePlant, project, ...plant}) => ({
      ...plant,
      projectName: (project as any)?.name,
      titlePhotoName: (basePlant as any)?.image_id,
      fields: {},
    })) as PlantWithProjectName[]
  })

  public getUserPlantsNamesWithId = cached(async () => {
    let {data: plantedPlantsNamesWithId, error} = await supabase
      .from('plantedPlants')
      .select('id, name, gardenPlanVariantId')
      .eq('userId', await currentUserId())
      .eq('isPlantDeleted', false)

    return plantedPlantsNamesWithId ?? []
  })

  public async getUserFilteredPlants(
    {
      page,
      perPage,
      filters,

      sort,
    }: {
      page: number
      perPage: number
      filters: PlantedPlantsFilters
      sort?: {field: string; direction: 'asc' | 'desc'}
    },
    language: Language
  ): Promise<{plants: PlantWithProjectName[]; totalCount: number}> {
    let query = supabase
      .from('planted_plants_data')
      .select(
        `id, name, plantedDate, photos, comment, plantBaseId, basePlant:plantBaseId( id, fields, image_id ), basePlantForFilter:plantBaseId(fields), overwrittenBaseFields, projectId, template, coordinates, userId, project:projectId( name ), collections:plantedPlantsVSCollection( ...plantedPlantsCollections(id, color, name) ), collectionsForFilter:plantedPlantsVSCollection( ...plantedPlantsCollections(id, color, name) ), test:plantBaseId(test:fields->"Жизненный цикл")`, // TODO: убрать test/test
        {count: 'exact'}
      )
      .eq('userId', await currentUserId())
      //.eq('isPlantDeleted', false)
      //.eq('journalEvents.type', 'planting')
      .range((page - 1) * perPage, page * perPage - 1)

    if (filters.projects) {
      query = query.filter(
        'projectId',
        'in',
        `(${filters.projects.map((e) => `"${e}"`).join(',')})`
      )
    }
    if (filters.plantName) {
      const plantNameQueryTerms = filters.plantName.split(' ').filter(Boolean)

      const nameFilterClauses = []

      for (const term of plantNameQueryTerms) {
        const orQueryForTerm = queryFields
          .map((field) => `fields->>"${field}".ilike.%${term}%`)
          .join(',')

        nameFilterClauses.push(orQueryForTerm)
      }

      query['url'].searchParams.append('basePlantForFilter.or', `(${nameFilterClauses.join(',')})`)

      query = query.or(`basePlantForFilter.not.is.null,name.ilike.%${filters.plantName}%`)
    }
    if (filters.startPlantingDate && !filters.endPlantingDate) {
      query = query.filter(
        'plantedDate',
        'gte',
        dayjs(filters.startPlantingDate).format('YYYY-MM-DD')
      )
    }
    if (filters.endPlantingDate && !filters.startPlantingDate) {
      query = query.filter(
        'plantedDate',
        'lte',
        dayjs(filters.endPlantingDate).format('YYYY-MM-DD')
      )
    }
    if (filters.startPlantingDate && filters.endPlantingDate) {
      query = query
        .filter('plantedDate', 'lte', dayjs(filters.endPlantingDate).format('YYYY-MM-DD'))
        .filter('plantedDate', 'gte', dayjs(filters.startPlantingDate).format('YYYY-MM-DD'))
    }
    if (filters.collections && filters.collections.length > 0) {
      query = query
        .filter(
          'collectionsForFilter.plantedPlantsCollections.id',
          'in',
          `(${filters.collections.map(String).join(',')})`
        )
        .filter('collectionsForFilter', 'not.is', 'null')
        .filter('collectionsForFilter.plantedPlantsCollections', 'not.is', 'null')
    }
    const lifecycle = filters.lifecycle || {}
    const lifecycleFilter = Object.keys(lifecycle).filter((key) => lifecycle[key])
    if (lifecycleFilter.length > 0) {
      const field = 'Жизненный цикл'

      const inFilter = `{${lifecycleFilter.map((n) => `"${n}"`).join(',')}}`
      // query = query.or(
      //   `overwrittenBaseFields->>'${field}'.${inFilter},basePlant.fields->>'${field}'.${inFilter}`
      // )
      //query = query.filter(`overwrittenBaseFields->>'${field}'`, 'in', inFilter)
      // query = query.or(
      //   `and(overwrittenBaseFields->>'${field}'.not.is.null,overwrittenBaseFields->>'${field}'.in.${inFilter}),basePlant=fields->>'${field}'.in.${inFilter}`
      // )

      //query = query.filter(`basePlant.fields->'${field}'?|[${inFilter}]`, 'is', 'true')
      // query.or(`basePlant.not.is.null,overwrittenBaseFields->'${field}'?|
      //   [${inFilter}].is.true`)

      //query = query.filter(`basePlant.fields->"Жизненный цикл"`, 'ov', inFilter)
      query = query.overlaps(`basePlant.fields->"Жизненный цикл"`, lifecycleFilter)
    }

    if (filters.hideGardenPlanVariantPlants) {
      const {data: gardenPlanVariantPlantIds} = await supabase
        .from('plantedPlants')
        .select('id')
        .eq('userId', await currentUserId())
        .not('gardenPlanVariantId', 'is', null)
      if (gardenPlanVariantPlantIds?.length) {
        query = query.not('id', 'in', `(${gardenPlanVariantPlantIds.map((p) => p.id).join(',')})`)
      }
    }

    if (sort?.field === 'project') {
      query = query.order('project(name)', {ascending: sort.direction === 'asc'})
    } else if (sort) {
      query = query.order(sort.field, {ascending: sort.direction === 'asc'})
    } else {
      query = query.order('name', {ascending: false})
    }

    query = query.order('id')

    const {data: plantedPlantsWithProjectName, count, error} = await query

    // the planted_plants_data view has no gardenPlanVariantId column
    const {data: gardenPlanVariantPlants} = await supabase
      .from('plantedPlants')
      .select('id, gardenPlanVariantId')
      .in(
        'id',
        (plantedPlantsWithProjectName ?? []).map((plant) => plant.id)
      )
      .not('gardenPlanVariantId', 'is', null)
    const gardenPlanVariantIdByPlantId = new Map(
      (gardenPlanVariantPlants ?? []).map((plant) => [plant.id, plant.gardenPlanVariantId])
    )
    const {data: gardenPlanVariants} = await supabase
      .from('gardenPlantVariants')
      .select('id, name')
      .in('id', [...new Set(gardenPlanVariantIdByPlantId.values())])
    const gardenPlanVariantNameById = new Map(
      (gardenPlanVariants ?? []).map((variant) => [variant.id, variant.name])
    )

    const plants = plantedPlantsWithProjectName?.map(
      ({basePlant, project, plantedDate, ...plant}) => {
        const userPlantFields = plant.overwrittenBaseFields || {}
        const basePlantFields = ((basePlant as any) || {})?.fields || {}

        const combinedPlantFields = prepareOverwrittenBaseFields(
          userPlantFields,
          basePlantFields,
          {language: language},
          true
        )

        return {
          ...plant,
          projectName: (project as any)?.name,
          titlePhotoName: (basePlant as any)?.image_id,
          fields: combinedPlantFields,
          dateOfPlantingUtc: plantedDate,
          gardenPlanVariantId: gardenPlanVariantIdByPlantId.get(plant.id) ?? null,
          gardenPlanVariantName: gardenPlanVariantNameById.get(
            gardenPlanVariantIdByPlantId.get(plant.id)
          ),
        }
      }
    ) as PlantWithProjectName[]

    return {plants, totalCount: count ?? 0}
  }

  public clearPlantListCaches() {
    this.getPlantById.clearAllCached()
    this.getProjectPlants.clearAllCached()
    this.getUserPlants.clearCache()
    this.getUserPlantsNamesWithId.clearCache()
    projectService.getUserProjectMaxPlantsInfoStatistics.clearCache()
    projectService.getUserProjectsInfoStatistics.clearCache()
  }

  public clearPlantCaches(plantId: number, projectId?: string) {
    this.getPlantById.clearCachedById(plantId)
    if (projectId) {
      this.getProjectPlants.clearCachedById(projectId)

      projectService.getUserProjectById.clearCachedById(projectId)
    }
  }

  public async createPlant(
    newPlant: Omit<Plant, 'id'>,
    newPhotos: [] = [],
    language: Language,
    basePlantId?: string
  ): Promise<{success: true; id: number} | {success: false; error: string}> {
    let plantPhotos: string[] = []
    if (newPhotos) {
      let result = await this.addPlantPhotos(newPhotos, newPlant.userId)

      if (result.success === false) {
        return {success: false, error: result.errors.map((e) => e.message).join(', ')}
      }
      plantPhotos = result.photos
    }

    let basePlantFields: Partial<DatabasePlantRecord['fields']> = {}
    if (basePlantId) {
      const basePlantResponse = await this.getPlantFromPlantDatabaseById(basePlantId)
      if (basePlantResponse.status === 'found') {
        basePlantFields = basePlantResponse.data.fields
      }
    }
    // newPlant.plantBaseId = currentBasePlant.id

    const overwrittenBaseFields = prepareOverwrittenBaseFields(
      newPlant.overwrittenBaseFields ?? {},
      basePlantFields,
      {language: language}
    )

    const result = await supabase
      .from('plantedPlants')
      .insert([
        {
          name: newPlant.name,
          //dateOfPlantingUtc: newPlant.dateOfPlantingUtc,
          photos: plantPhotos,
          comment: newPlant.comment,
          projectId: newPlant.projectId,
          gardenPlanVariantId: newPlant.gardenPlanVariantId || null,
          userId: await currentUserId(),
          elementId: newPlant.elementId,
          plantBaseId: basePlantId,
          overwrittenBaseFields,
        },
      ])
      .select('id')
    //await new Promise((resolve) => setTimeout(resolve, 2000))

    if (result.error !== null) {
      return {success: false, error: result.error.message}
    } else {
      if (newPlant.dateOfPlantingUtc) {
        const resultPlantingEvent = await journalService.createJournalEvent({
          type: {type: 'planting'},
          actualCompletionDateUtc: (dayjs(newPlant.dateOfPlantingUtc) as any).utc(true),
          plant: {id: result.data[0].id, name: ''},
        })
      } //нет проверки на случай ошибки при создании ивента

      this.clearPlantListCaches()
      return {success: true, id: result.data[0].id}
    }
  }

  public async editPlant(
    currentPlant: Plant,
    language: Language,
    photos?: string[],
    newPhotos: [] = []
  ) {
    // TODO: what if one photo removed but other is added?

    let basePlantFields: Partial<DatabasePlantRecord['fields']> = {}
    if (currentPlant.plantBaseId) {
      const basePlantResponse = await this.getPlantFromPlantDatabaseById(currentPlant.plantBaseId)
      if (basePlantResponse.status === 'found') {
        basePlantFields = basePlantResponse.data.fields
      }
    }

    const plant: Plant = {
      id: currentPlant.id,
      name: currentPlant.name,
      comment: currentPlant.comment,
      coordinates: currentPlant.coordinates,
      dateOfPlantingUtc: currentPlant.dateOfPlantingUtc,
      elementId: currentPlant.elementId,
      overwrittenBaseFields: prepareOverwrittenBaseFields(
        currentPlant.overwrittenBaseFields ?? {},
        basePlantFields,
        {language: language}
      ),
      photos: currentPlant.photos || [],
      plantBaseId: currentPlant.plantBaseId,
      projectId: currentPlant.projectId,
      userId: currentPlant.userId,
      confidenceInPlantVariety: currentPlant.confidenceInPlantVariety,
    }
    if (photos && currentPlant.photos && photos.length < currentPlant.photos.length) {
      let deletedPhotos = currentPlant.photos.filter((photo) => !photos.includes(photo))
      let result = await this.deletePlantPhotos(deletedPhotos, currentPlant.userId, currentPlant.id)

      if (result.success === false) {
        return {success: false, error: result.error.message}
      } else {
        plant.photos = photos
      }
    }

    if (newPhotos) {
      let result = await this.addPlantPhotos(newPhotos, currentPlant.userId)

      if (result.success === false) {
        return {success: false, error: result.errors.map((e) => e.message).join(', ')}
      } else {
        plant.photos = [...(plant.photos ?? []), ...result.photos]
      }
    }

    const {data, error} = await supabase
      .from('plantedPlants')
      .update([plant])
      .eq('id', currentPlant.id)

    if (error) {
      return {success: false, error: error.message}
    } else {
      this.clearPlantListCaches()
      this.clearPlantCaches(plant.id, plant.projectId)

      return {success: true}
    }
  }

  public async editConfidenceInPlantVarietyByPlantId(plantId: number, confidense: boolean) {
    const {data, error} = await supabase
      .from('plantedPlants')
      .update({confidenceInPlantVariety: confidense})
      .eq('id', plantId)

    if (error) {
      return {success: false, error: error.message}
    } else {
      this.getPlantById.clearCachedById(plantId)
      return {success: true}
    }
  }

  public async editPlantTemplateByPlantId(plantId: number, tempateId: string) {
    const {data, error} = await supabase
      .from('plantedPlants')
      .update({template: tempateId})
      .eq('id', plantId)

    if (error) {
      return {success: false, error: error.message}
    } else {
      this.getPlantById.clearCachedById(plantId)
      this.clearPlantListCaches()
      return {success: true}
    }
  }

  public async editPlantProjectByPlantId(plantId: number, projectId: string) {
    const {data, error} = await supabase
      .from('plantedPlants')
      .update({projectId: projectId, elementId: null, coordinates: []})
      .eq('id', plantId)

    if (error) {
      return {success: false, error: error.message}
    } else {
      this.getPlantById.clearCachedById(plantId)
      this.clearPlantListCaches()
      return {success: true}
    }
  }

  public async deletePlant(
    currentPlantId: number,
    isDeletePlantEvents: boolean,
    projectId?: string
  ) {
    const {data, error} = await supabase.functions.invoke(`delete-plant`, {
      body: {
        plantId: currentPlantId,
        alsoRemoveEvents: isDeletePlantEvents,
      },
    })

    if (error) {
      return {success: false, error}
    } else {
      this.clearPlantListCaches()
      this.clearPlantCaches(currentPlantId, projectId)
      return {success: true}
    }
  }

  // public async deletePlantsByProjectId(projectId: number) {
  //   const {data, error} = await supabase
  //     .from('plantedPlants')
  //     .update({isPlantDeleted: true})
  //     .eq('projectId', projectId)
  //   if (error) {
  //     return {success: false, error}
  //   } else {
  //     this.clearPlantListCaches()
  //     return {success: true}
  //   }
  // }

  public async placePlantOnCanvas(currentPlantId: number) {
    //переписать
    // const {data, error} = await supabase.from('plantedPlants').delete().eq('id', currentPlantId)
    // if (error) {
    //   return {success: false, error}
    // } else {
    //   return {success: true}
    // }
    return {success: true, error: null}
  }

  public async removePlantFromCanvas(
    currentPlantId: number,
    currentPlantCoordinates: NodeConfig[],
    specimenNumber: number
  ) {
    const newCoordinates =
      typeof specimenNumber === 'number'
        ? currentPlantCoordinates.map((coords, index) => (index === specimenNumber ? {} : coords))
        : currentPlantCoordinates.length > 1
        ? currentPlantCoordinates.map((coords) => ({}))
        : []
    const {data, error} = await supabase
      .from('plantedPlants')
      .update({
        coordinates: newCoordinates,
      })
      .eq('id', currentPlantId)
    if (error) {
      return {success: false, error}
    } else {
      this.clearPlantListCaches()
      this.getPlantById.clearCachedById(currentPlantId)
      return {success: true}
    }
  }

  public async connectSolitariusTemplateToPlant(
    solitariusTemplateElement: ElementWithVisibility,
    plantId: number,
    projectId: string,
    projectPlants: Plant[],
    elements: TypedElement[]
  ) {
    const connectedPlantOldCoordinates = projectPlants.find(
      (plant) => plant.id === plantId
    )?.coordinates

    let newCoordinates = null

    if (!connectedPlantOldCoordinates || connectedPlantOldCoordinates.length === 0) {
      newCoordinates = [
        {
          x: solitariusTemplateElement.props.x,
          y: solitariusTemplateElement.props.y,
          width: solitariusTemplateElement.props.width,
          height: solitariusTemplateElement.props.height,
        },
      ]
    } else {
      newCoordinates = [
        ...connectedPlantOldCoordinates,
        {
          x: solitariusTemplateElement.props.x,
          y: solitariusTemplateElement.props.y,
          width: solitariusTemplateElement.props.width,
          height: solitariusTemplateElement.props.height,
        },
      ]
    }

    const newProjectElements = elements.filter(
      (element) => element.elementId !== solitariusTemplateElement.elementId
    )

    const {data, error} = await supabase
      .from('plantedPlants')
      .update({template: solitariusTemplateElement.elementId, coordinates: newCoordinates})
      .eq('id', plantId)

    if (error) {
      return {success: false, error: error.message}
    } else {
      const {data, error} = await supabase
        .from('projects')
        .update({elements: newProjectElements})
        .eq('id', projectId)

      if (error) {
        return {success: false, error: error.message}
      } else {
        this.clearPlantListCaches()
        this.clearPlantCaches(plantId, projectId)
        return {success: true}
      }
    }
  }

  public async addPlantSpecimen(
    currentPlantId: number,
    currentPlantProjectId: string,
    currentPlantCoordinates: NodeConfig[],
    newSpecimenCoordinates?: NodeConfig[]
  ) {
    const newCoordinates = newSpecimenCoordinates
      ? [...currentPlantCoordinates, ...newSpecimenCoordinates]
      : currentPlantCoordinates.length !== 0
      ? [...currentPlantCoordinates, {}]
      : [{}, {}]
    const {data, error} = await supabase
      .from('plantedPlants')
      .update({
        coordinates: newCoordinates,
      })
      .eq('id', currentPlantId)
    if (error) {
      return {success: false, error}
    } else {
      this.clearPlantListCaches()
      this.clearPlantCaches(currentPlantId, currentPlantProjectId)
      return {success: true}
    }
  }

  public async removePlantSpecimen(
    currentPlantId: number,
    currentPlantProjectId: string,
    currentPlantCoordinates: NodeConfig[],
    specimenNumber: number
  ) {
    const newCoordinates = typeof specimenNumber === 'number' ? currentPlantCoordinates : []
    newCoordinates.splice(specimenNumber, 1)
    const {data, error} = await supabase
      .from('plantedPlants')
      .update({
        coordinates: newCoordinates,
      })
      .eq('id', currentPlantId)
    if (error) {
      return {success: false, error}
    } else {
      this.clearPlantListCaches()
      this.clearPlantCaches(currentPlantId, currentPlantProjectId)
      return {success: true}
    }
  }

  public async movePlantOnCanvas(currentPlantId: number) {
    //переписать
    // const {data, error} = await supabase.from('plantedPlants').delete().eq('id', currentPlantId)

    // if (error) {
    //   return {success: false, error}
    // } else {
    //   return {success: true}
    // }
    return {success: true, error: null}
  }

  public async getPlantImageURLs(plantNames: string[] = []) {
    if (plantNames.length === 0) {
      return {success: true, urls: []}
    }
    const userId = await currentUserId()
    const {data, error} = await supabase.storage.from('photos').createSignedUrls(
      plantNames.map((plantName) => `${userId}/${plantName}`),
      60
    )

    if (error) {
      return {success: false, error}
    } else {
      return {success: true, urls: data.map((element) => element.signedUrl)}
    }
  }

  public async getPlantDatabaseImageURLs(plantBaseId: string, plantNames: string[] = []) {
    if (plantNames.length === 0) {
      return {success: true, urls: []}
    }

    const plantNamesPaths = plantNames.map((name) => `${plantBaseId}/${name}`)

    const {data, error} = await supabase.functions.invoke(`plants-db-get-link-bulk`, {
      body: {
        paths: plantNamesPaths,
      },
    })

    if (error) {
      return {success: false, error}
    } else {
      return {success: true, urls: data.map((element: {url: string}) => element.url)}
    }
  }

  public async addPlantPhotos(plantPhoto: any[], userId: string) {
    const errors = [] as Error[]
    const photosNames = [] as string[]
    if (plantPhoto.length != 0) {
      for (let photo of plantPhoto) {
        const name = Date.now().toString()
        photosNames.push(name)
        const {data, error} = await supabase.storage
          .from('photos')
          .upload(userId + '/' + name, photo.originFileObj, {
            cacheControl: '3600',
            upsert: false,
          })
        if (error) {
          errors.push(error)
        }
      }
    }

    if (errors.length !== 0) {
      return {success: false as const, errors}
    } else {
      return {success: true as const, photos: photosNames}
    }
  }

  public async deletePlantPhotos(plantPhoto: string[], userId: string, plantId: number) {
    const {data, error} = await supabase.storage
      .from('photos')
      .remove(plantPhoto.map((photo) => userId + '/' + photo))

    if (error) {
      return {success: false as const, error}
    } else {
      return {success: true as const}
    }
  }

  public async getTitlePlantPhoto(plantTitlePhotoName: string, userPlantPhoto?: string) {
    if (userPlantPhoto) {
      const {data, error} = await supabase.storage
        .from('photos')
        .createSignedUrl(`${await currentUserId()}/${userPlantPhoto}`, 60)

      if (error) {
        return urlProjectPhoto0
      } else {
        return data.signedUrl
      }
    } else if (plantTitlePhotoName) {
      return supabase.storage.from('plant_db_thumbnails').getPublicUrl(plantTitlePhotoName).data
        .publicUrl
    } else {
      return urlProjectPhoto0
    }
  }

  public async createPlantCollection(plantCollectionName: string) {
    const {data, error} = await supabase
      .from('plantedPlantsCollections')
      .insert([
        {
          name: plantCollectionName,
          userId: await currentUserId(),
        },
      ])
      .select()
    //await new Promise((resolve) => setTimeout(resolve, 2000))

    if (error) {
      return {success: false, error: error.message}
    } else {
      return {success: true, newCollection: data[0] as PlantCollection}
    }
  }

  public async deletePlantCollection(deletedPlantCollectionId: number) {
    const {data, error} = await supabase
      .from('plantedPlantsCollections')
      .delete()
      .eq('id', deletedPlantCollectionId)

    if (error) {
      return {success: false, error: error.message}
    } else {
      this.clearPlantListCaches()
      return {success: true}
    }
  }

  public async getUserPlantCollections() {
    let {data, error} = await supabase
      .from('plantedPlantsCollections')
      .select('id, name, color, userId')
      .eq('userId', await currentUserId())

    return data as PlantCollection[]
  }

  public async getUserPlantCollectionsWithPlantsIds() {
    let {data, error} = await supabase
      .from('plantedPlantsCollections')
      .select('id, name, color, userId, plantsIds:plantedPlantsVSCollection( plantedPlantsId)')
      .eq('userId', await currentUserId())

    return data as PlantCollection[]
  }

  public async changePlantCollections(
    plantId: number,
    newPlantCollectionsIds: number[],
    oldPlantCollectionsIds: number[]
  ) {
    // TODO: use edge function for that because we need to insert/delete multiple records in one transaction
    if (newPlantCollectionsIds.length > oldPlantCollectionsIds.length) {
      let plantIdcolId = {
        plantedPlantsId: plantId,
        collectionId: newPlantCollectionsIds[newPlantCollectionsIds.length - 1],
      }
      const {data, error} = await supabase.from('plantedPlantsVSCollection').insert(plantIdcolId)

      if (error) {
        return {success: false, error: error.message}
      } else {
        this.clearPlantCaches(plantId)
        return {success: true}
      }
    } else if (newPlantCollectionsIds.length < oldPlantCollectionsIds.length) {
      const deletedCollectionId = oldPlantCollectionsIds.filter((value) => {
        if (!newPlantCollectionsIds.includes(value)) {
          return value
        }
      })
      const {data, error} = await supabase
        .from('plantedPlantsVSCollection')
        .delete()
        .eq('plantedPlantsId', plantId)
        .eq('collectionId', deletedCollectionId)

      //await new Promise((resolve) => setTimeout(resolve, 2000))

      if (error) {
        return {success: false, error: error.message}
      } else {
        this.clearPlantCaches(plantId)
        return {success: true}
      }
    }
  }

  public async searchPlantsDatabase(query: string): Promise<
    | {success: false; error: string}
    | {
        success: true
        data: {
          data: {
            records: DatabasePlantRecord[]
          }
        }
      }
  > {
    const queryTerms = query.split(' ').filter(Boolean)

    let databaseQuery = supabase.from('plant_database').select('id,fields,image_id,image_bucket')

    for (const term of queryTerms) {
      const orQueryForTerm = queryFields
        .map((field) => `fields->>"${field}".ilike.%${term}%`)
        .join(',')

      databaseQuery.or(orQueryForTerm)
    }

    const {data, error} = await databaseQuery

    if (error) {
      return {success: false, error: error.message}
    } else {
      return {
        success: true,
        data: {
          data: {
            records: data
              .map((element) => ({
                id: element.id,
                fields: element.fields,
                thumbnailUrl: supabase.storage
                  .from(element.image_bucket)
                  .getPublicUrl(element.image_id).data.publicUrl,
              }))
              .sort((x, y) => {
                const xVarietyLength = x.fields[varietyField]?.length || 0
                const yVarietyLength = y.fields[varietyField]?.length || 0

                const varietyComparison = xVarietyLength - yVarietyLength

                if (varietyComparison != 0) {
                  return varietyComparison
                }

                const xCommonNameLength = x.fields[commonNameField]?.length || 0
                const yCommonNameLength = y.fields[commonNameField]?.length || 0

                return xCommonNameLength - yCommonNameLength
              }),
          },
        },
      }
    }
  }

  public getPlantFromPlantDatabaseById = cachedById<
    | {status: 'error'; error: string}
    | {
        status: 'found'
        data: DatabasePlantRecord
      }
    | {status: 'notFound'},
    string
  >(async (id: string) => {
    const {data, error} = await supabase
      .from('plant_database')
      .select('id, fields, image_id, image_bucket')
      .eq('id', id)

    if (error) {
      return {status: 'error', error: error.message}
    } else if (data.length !== 1) {
      return {status: 'notFound'}
    } else {
      return {
        status: 'found',
        data: {
          id: data[0].id,
          fields: data[0].fields,
          thumbnailUrl: supabase.storage.from(data[0].image_bucket).getPublicUrl(data[0].image_id)
            .data.publicUrl,
        },
      }
    }
  })
}

export const DatabaseMonths = [
  'январь' as const,
  'февраль' as const,
  'март' as const,
  'апрель' as const,
  'май' as const,
  'июнь' as const,
  'июль' as const,
  'август' as const,
  'сентябрь' as const,
  'октябрь' as const,
  'ноябрь' as const,
  'декабрь' as const,
]
export type DatabaseMonth = ArrayElementOf<typeof DatabaseMonths>
export type DatabaseMonthWithTemp = DatabaseMonth | 'после минования угрозы заморозков'

export type DatabasePlantRecord = {
  id: string
  thumbnailUrl?: string
  fields: {
    'Общераспространенное название'?: string
    'Другие названия'?: string
    'Уточняющая характеристика'?: string
    'Научное (латинское) название'?: string
    'Фотографии растения'?: string[]
    'Описание сорта'?: string
    Сорт_eng?: string
    Сорт_ru?: string
    Семейство_ru?: string
    Семейство_la?: string
    'Зоны морозостойкости (USDA-зоны)'?: string[]
    'Достоинства сорта'?: string
    'Недостатки сорта'?: string
    'Сложность ухода'?: string
    'Фотографии листьев'?: string[]
    'Фотографии цветов'?: string[]
    'Фотографии плодов'?: string[]
    'Назначение/местоположение'?: string[]
    'Жизненный цикл'?: string[]
    Полив?: string
    'Отношение к освещенности'?: string[]
    'Цвет цветов'?: string
    'Цвет листьев'?: string
    'Высота от (м)'?: number
    'Высота до (м)'?: number
    'Раскидистость от (м)'?: number
    'Раскидистость до (м)'?: number
    'Размер цветов от (см)'?: number
    'Размер цветов до (см)'?: number
    'Масса плодов (г)'?: number
    Съедобность?: string[]
    'Медицинские свойства'?: string
    Ядовитость?: string[]
    Тип?: string[]
    'Факторы привлекательности'?: string[]
    'Время цветения'?: DatabaseMonth[]
    'Особенности выращивания'?: string[]
    'Способы размножения'?: string[]
    'Время посадки на рассаду в закрытый грунт'?: DatabaseMonth[]
    'Время посадки на рассаду в открытый грунт'?: DatabaseMonthWithTemp[]
    'Глубина посадки (см)'?: string
    'Время плодоношения'?: DatabaseMonth[]
    'Время обрезки'?: DatabaseMonth[]
    'Дополнительные действия по уходу': string[]
    'Применение в дизайне'?: string[]
    'Скорость роста'?: string
    'Почва (субстрат)'?: string
    Болезни?: string[]
    Вредители?: string[]
    'Частота обработки пестицидами'?: string[]
    Пестициды?: string
    Удобрения?: string
    'Частота внесения удобрений'?: string[]
    'Особенности хранения плодов'?: string
    'Особенности хранения посадочного материала'?: string
  }
}

export const instance = new PlantsService()
